import React, { useState, useEffect, useCallback, useRef } from 'react';
import LookEditor from './LookEditor';
import FavoritesLayoutEditor from './FavoritesLayout';
import ManageLooks from './ManageLooks';
import PromoImageGenerator from '../PromoImageGenerator';
import { fetchLooks, fetchLookPurchaseCounts, updateLook } from './shared/api';
import generateThumbnail from './shared/generateThumbnail';
import { FullPageSpinner } from './shared/Spinner';

// Error boundary to prevent white screens from uncaught render errors
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('FavoritesDesigner error:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 16, color: '#d32f2f', marginBottom: 12 }}>Something went wrong.</div>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>{this.state.error.message}</div>
          <button
            onClick={() => this.setState({ error: null })}
            style={{ padding: '8px 24px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// "Publish All Seasons" — publishes every LookTemplate so it becomes
// visible to the EC2 image processor through the image_processing_specs
// view. Until per-season publish UI lands, this is the only Publish action.
const PublishAllButton = () => {
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleClick = async () => {
    if (!window.confirm('Publish ALL look templates across all seasons? Published templates become live to the EC2 image processor.')) return;
    setBusy(true);
    setFeedback(null);
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch('/admin/favorites_designer/publish_all_seasons', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
        credentials: 'same-origin'
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFeedback(`Published ${data.count} templates at ${new Date(data.published_at).toLocaleTimeString()}`);
      setTimeout(() => setFeedback(null), 5000);
    } catch (e) {
      setFeedback(`Publish failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
      {feedback && (
        <span style={{ fontSize: 12, color: feedback.startsWith('Publish failed') ? '#d32f2f' : '#388e3c' }}>
          {feedback}
        </span>
      )}
      <button
        onClick={handleClick}
        disabled={busy}
        style={{
          padding: '8px 16px',
          background: busy ? '#9e9e9e' : '#388e3c',
          color: '#fff', border: 'none',
          borderRadius: 4, cursor: busy ? 'progress' : 'pointer',
          fontSize: 13, fontWeight: 500
        }}
      >
        {busy ? 'Publishing…' : 'Publish All Seasons'}
      </button>
    </div>
  );
};

const TABS = [
  { id: 'layout', label: 'Favorites Layout' },
  { id: 'editor', label: 'Look Editor' },
  { id: 'manage', label: 'Manage Looks' },
  // Card art for product tiles (#5444). The same grid as Manage Looks over a
  // narrower set, not a second list to keep in step: a look can be a card image AND
  // a style customers buy, so this is a section, never a separate kind of thing.
  { id: 'hero', label: 'Hero Layouts' },
  { id: 'promo', label: 'Promotional Images' },
];

// Chooser modal shown when the user clicks "+ New Look" — decides whether the
// blank canvas is a Favorites Look (existing LookEditor flow) or a Promotional
// Image (opens the Promo Templates tab in template-builder mode).
const NewLookChooser = ({ onPick, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ background: '#fff', borderRadius: 8, padding: 20, width: 460, maxWidth: '95%' }}
    >
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>What are you creating?</div>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 14 }}>
        Pick the tool that matches what you need. You can always switch tabs later.
      </div>
      {[
        { id: 'favorites', title: 'Favorites Look', body: 'A single look for the favorites layout — canvas, overlay, guides, text.' },
        { id: 'promo',     title: 'Promotional Image', body: 'A static image or animated GIF for promo campaigns — multiple frames, per-frame background.' },
      ].map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onPick(opt.id)}
          style={{
            display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px',
            marginBottom: 10, border: '1px solid #ddd', borderRadius: 6, background: '#fff',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1976d2'; e.currentTarget.style.background = '#f5f9ff'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ddd'; e.currentTarget.style.background = '#fff'; }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: '#0d47a1' }}>{opt.title}</div>
          <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{opt.body}</div>
        </button>
      ))}
      <button
        type="button"
        onClick={onClose}
        style={{
          marginTop: 4, background: 'none', border: 'none', color: '#666',
          fontSize: 12, cursor: 'pointer',
        }}
      >
        Cancel
      </button>
    </div>
  </div>
);

const FavoritesDesigner = () => {
  const [activeTab, setActiveTab] = useState('layout');
  const [looks, setLooks] = useState([]);
  const [editingLook, setEditingLook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [childDirty, setChildDirty] = useState(false);
  const [referencePoses, setReferencePoses] = useState([]);
  const [purchaseCounts, setPurchaseCounts] = useState({});
  const dirtyRef = useRef(false);
  // Track which tabs have been visited so we can lazy-mount them
  const [mountedTabs, setMountedTabs] = useState({ layout: true });

  // Keep ref in sync for beforeunload handler
  useEffect(() => {
    dirtyRef.current = childDirty;
  }, [childDirty]);

  // Browser beforeunload warning
  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const loadLooks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLooks();
      setLooks(data);

      // Auto-regenerate thumbnails that are missing, stale JPEGs, or too small (old 150px).
      const MIN_THUMB_LENGTH = 20000; // approximate data URL length for a 480px PNG thumb
      const needsThumb = data.filter(l => {
        if (!l.ref_photo_data && !l.ref_photo_thumb) return false; // no source data at all
        if (!l.ref_photo_thumb) return !!l.ref_photo_data; // missing thumb, has source
        if (l.ref_photo_thumb.startsWith('data:image/jpeg')) return true; // stale JPEG
        if (l.ref_photo_data && l.ref_photo_thumb.length < MIN_THUMB_LENGTH) return true; // too small, needs upsize
        return false;
      });

      if (needsThumb.length > 0) {
        const source = (look) => look.ref_photo_data || look.ref_photo_thumb;
        Promise.all(needsThumb.map(async (look) => {
          try {
            const thumb = await generateThumbnail(source(look));
            if (!thumb) return null;
            await updateLook(look.id, { ref_photo_thumb: thumb });
            return { id: look.id, ref_photo_thumb: thumb };
          } catch (err) {
            console.error(`Failed to regenerate thumbnail for look ${look.id}:`, err);
            return null;
          }
        })).then(results => {
          const updated = results.filter(Boolean);
          if (updated.length > 0) {
            setLooks(prev => prev.map(l => {
              const u = updated.find(r => r.id === l.id);
              return u ? { ...l, ref_photo_thumb: u.ref_photo_thumb } : l;
            }));
          }
        });
      }
    } catch (err) {
      console.error('Failed to load looks:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadLooks(); }, [loadLooks]);

  const loadPurchaseCounts = useCallback(async () => {
    try {
      const counts = await fetchLookPurchaseCounts();
      setPurchaseCounts(counts || {});
    } catch (err) {
      console.error('Failed to load purchase counts:', err);
    }
  }, []);

  // Only fetch purchase counts when the Manage Looks tab is active
  useEffect(() => {
    if (activeTab !== 'manage') return;
    loadPurchaseCounts();
    const interval = setInterval(loadPurchaseCounts, 60000);
    return () => clearInterval(interval);
  }, [activeTab, loadPurchaseCounts]);

  // Guarded tab switch: confirm if dirty, lazy-mount on first visit
  const switchTab = useCallback((tabId) => {
    if (childDirty && !window.confirm('You have unsaved changes. If you leave, your changes will be lost.')) {
      return;
    }
    setMountedTabs(prev => prev[tabId] ? prev : { ...prev, [tabId]: true });
    setActiveTab(tabId);
  }, [childDirty]);

  const handleEditLook = (look) => {
    if (childDirty && !window.confirm('You have unsaved changes. If you leave, your changes will be lost.')) {
      return;
    }
    setEditingLook(look);
    setMountedTabs(prev => prev.editor ? prev : { ...prev, editor: true });
    setActiveTab('editor');
  };

  // "+ New Look" now first prompts for the type (favorites look vs promo image).
  const [showNewChooser, setShowNewChooser] = useState(false);
  const handleNewLook = () => {
    if (childDirty && !window.confirm('You have unsaved changes. If you leave, your changes will be lost.')) {
      return;
    }
    setShowNewChooser(true);
  };
  const handleChooseNewType = (choice) => {
    setShowNewChooser(false);
    if (choice === 'favorites') {
      setEditingLook(null);
      setMountedTabs((prev) => (prev.editor ? prev : { ...prev, editor: true }));
      setActiveTab('editor');
    } else if (choice === 'promo') {
      setMountedTabs((prev) => (prev.promo ? prev : { ...prev, promo: true }));
      setActiveTab('promo');
    }
  };

  // Open the editor pre-populated with a copy of `look` as a NEW (unsaved)
  // record: strip ids and drop the pose number so the user is nudged to pick
  // a fresh one. Name is uniquely-scoped per season so we append " (Copy)".
  const handleDuplicateLook = (look) => {
    if (childDirty && !window.confirm('You have unsaved changes. If you leave, your changes will be lost.')) {
      return;
    }
    const dup = {
      ...look,
      id: undefined,
      name: `${look.name || 'Untitled'} (Copy)`,
      pose_number: null,
      created_at: undefined,
      updated_at: undefined,
      text_fields: (look.text_fields || []).map((tf) => ({ ...tf, id: undefined })),
    };
    setEditingLook(dup);
    setMountedTabs((prev) => (prev.editor ? prev : { ...prev, editor: true }));
    setActiveTab('editor');
  };

  const handleLookSaved = (savedLook, isNew) => {
    setLooks(prev => {
      const idx = prev.findIndex(l => l.id === savedLook.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = savedLook;
        return updated;
      }
      return [...prev, savedLook];
    });

    setChildDirty(false);

    if (isNew) {
      // New look → go to Favorites Layout tab to assign it to slots
      setEditingLook(savedLook);
      setMountedTabs(prev => prev.layout ? prev : { ...prev, layout: true });
      setActiveTab('layout');
    } else {
      // Existing look updated → go back to Manage Looks tab
      setEditingLook(null);
      setMountedTabs(prev => prev.manage ? prev : { ...prev, manage: true });
      setActiveTab('manage');
    }
  };

  const handleLookDeleted = (id) => {
    setLooks(prev => prev.filter(l => l.id !== id));
    if (editingLook?.id === id) {
      setEditingLook(null);
      setActiveTab('manage');
    }
  };

  // Curation toggle result — the server already persisted and re-exported the
  // season's CSV, so this just syncs the flag into local state.
  const handleLookActiveChanged = (id, active) => {
    setLooks(prev => prev.map(l => (l.id === id ? { ...l, active } : l)));
  };

  const handleLookHeroChanged = (id, heroLayout) => {
    setLooks((prev) => prev.map((l) => (l.id === id ? { ...l, hero_layout: heroLayout } : l)));
  };

  const handleDirtyChange = useCallback((isDirty) => {
    setChildDirty(isDirty);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Tab bar */}
      <div style={{
        display: 'flex', borderBottom: '2px solid #e0e0e0', background: '#fafafa',
        padding: '0 16px'
      }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => switchTab(tab.id)}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid #1976d2' : '3px solid transparent',
              background: 'none',
              color: activeTab === tab.id ? '#1976d2' : '#666',
              fontWeight: activeTab === tab.id ? 600 : 400,
              fontSize: 14,
              cursor: 'pointer',
              marginBottom: -2
            }}
          >
            {tab.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {activeTab !== 'editor' && (
          <PublishAllButton />
        )}
        {activeTab !== 'editor' && (
          <button
            onClick={handleNewLook}
            style={{
              padding: '8px 16px', margin: '6px 0 6px 8px',
              background: '#1976d2', color: '#fff', border: 'none',
              borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500
            }}
          >
            + New Look
          </button>
        )}
      </div>

      {/* Tab content — all panels stay mounted, hidden via CSS to preserve state */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(255,255,255,0.7)', zIndex: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <FullPageSpinner label="Loading looks..." />
          </div>
        )}
        <ErrorBoundary>
          {mountedTabs.layout && (
            <div style={{ display: activeTab === 'layout' ? 'flex' : 'none', height: '100%', flexDirection: 'column' }}>
              <FavoritesLayoutEditor looks={looks} loading={loading} onDirtyChange={handleDirtyChange} onReferencePosesChange={setReferencePoses} />
            </div>
          )}
          {mountedTabs.editor && (
            <div style={{ display: activeTab === 'editor' ? 'flex' : 'none', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
              <LookEditor
                look={editingLook}
                looks={looks}
                referencePoses={referencePoses}
                onSaved={handleLookSaved}
                onBack={() => switchTab('manage')}
                onDirtyChange={handleDirtyChange}
              />
            </div>
          )}
          {mountedTabs.manage && (
            <div style={{ display: activeTab === 'manage' ? 'block' : 'none', height: '100%', overflow: 'auto' }}>
              <ManageLooks
                looks={looks}
                loading={loading}
                onEdit={handleEditLook}
                onDuplicate={handleDuplicateLook}
                onDeleted={handleLookDeleted}
                onActiveChanged={handleLookActiveChanged}
                onHeroChanged={handleLookHeroChanged}
                onNew={handleNewLook}
                purchaseCounts={purchaseCounts}
              />
            </div>
          )}
          {mountedTabs.hero && (
            <div style={{ display: activeTab === 'hero' ? 'block' : 'none', height: '100%', overflow: 'auto' }}>
              <ManageLooks
                heroOnly
                looks={looks}
                loading={loading}
                onEdit={handleEditLook}
                onDuplicate={handleDuplicateLook}
                onDeleted={handleLookDeleted}
                onActiveChanged={handleLookActiveChanged}
                onHeroChanged={handleLookHeroChanged}
                onNew={handleNewLook}
                purchaseCounts={purchaseCounts}
              />
            </div>
          )}
          {mountedTabs.promo && (
            <div style={{ display: activeTab === 'promo' ? 'flex' : 'none', flexDirection: 'column', height: '100%', minHeight: 0 }}>
              <PromoImageGenerator onDirtyChange={handleDirtyChange} />
            </div>
          )}
        </ErrorBoundary>
      </div>

      {showNewChooser && (
        <NewLookChooser
          onPick={handleChooseNewType}
          onClose={() => setShowNewChooser(false)}
        />
      )}
    </div>
  );
};

export default FavoritesDesigner;
